import type { FC } from 'react';
import { motion } from 'framer-motion';

import { heroReveal } from '#/lib/hero-reveal';
import type { SanityHeroCentered } from '#/lib/sanity/queries/heroCentered';
import { createSanityFieldDataAttribute } from '#/lib/sanity/visualEditing';
import {
  getSectionButtonTheme,
  getSectionThemeAccent,
  getSectionThemeSurface,
  isDarkSectionTheme,
} from '#/lib/section-theme';
import type { PageLayoutSharedProps } from '#/lib/types';
import { cn } from '#/lib/utils';
import { BasicText } from '#/components/BasicText';
import { SectionMascot } from '#/components/BlobMascot';
import { ButtonRow } from '#/components/ButtonRow';
import { formatHeadline } from '#/components/Headline';
import {
  getHeroBackground,
  HeroBackgroundGraphic,
} from '#/components/HeroBackgroundGraphic';

export const HeroCentered: FC<SanityHeroCentered & PageLayoutSharedProps> = (
  props,
) => {
  const isDark = isDarkSectionTheme(props.theme);
  const isFirst = props.sectionIndex === 0;
  const accentClass = getSectionThemeAccent(props.theme);
  const buttonTheme = getSectionButtonTheme(props.theme);
  const background = getHeroBackground(props.theme, props.backgroundPattern);

  return (
    <div
      className={cn(
        'p-main text-center relative isolate overflow-hidden',
        getSectionThemeSurface(props.theme),
        isFirst
          ? 'mt-[calc(var(--header-height)*-1)] pb-100 pt-[calc(var(--header-height)+8rem)] m:pb-150'
          : 'py-80 m:py-150',
      )}
      style={background ? { backgroundColor: background.baseColor } : undefined}
    >
      {background ? <HeroBackgroundGraphic background={background} /> : null}
      <SectionMascot preset={props.mascot} />
      <div className="relative z-[1] mx-auto flex max-w-900 flex-col items-center gap-20 m:gap-28">
        {props.eyebrow ? (
          <motion.p
            {...heroReveal(0)}
            className={cn('type-eyebrow', accentClass)}
            data-sanity={createSanityFieldDataAttribute(
              props.sanitySource,
              'eyebrow',
            )}
          >
            {props.eyebrow}
          </motion.p>
        ) : null}

        <motion.h1
          {...heroReveal(1)}
          className="type-heading3 m:type-heading2 l:type-heading1 text-balance"
          data-sanity={createSanityFieldDataAttribute(
            props.sanitySource,
            'title',
          )}
        >
          {formatHeadline(props.title, accentClass)}
        </motion.h1>

        {props.description ? (
          <motion.div {...heroReveal(2)}>
            <BasicText
              value={props.description}
              highlightClass={accentClass}
              className={cn(
                'type-paragraph m:type-paragraph-lg mx-auto max-w-[680px] text-balance',
                isDark ? 'text-cloud' : 'text-grayscale-05',
              )}
            />
          </motion.div>
        ) : null}

        {props.buttons?.length ? (
          <motion.div
            {...heroReveal(3)}
            className="@container mt-8 flex w-full justify-center"
            data-sanity={createSanityFieldDataAttribute(
              props.sanitySource,
              'buttons',
            )}
          >
            <ButtonRow buttons={props.buttons} theme={buttonTheme} />
          </motion.div>
        ) : null}
      </div>
    </div>
  );
};
